// Installing the app shell.
//
// Registration is deliberately fire-and-forget: nothing in the platform depends on the worker, so a
// browser that refuses it (private windows, older Safari, a locked-down school network) loses the
// install button and nothing else.

const isLocalhost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;

  // Not in dev. The worker caches nothing so it would not actually break HMR, but a worker running
  // against a dev server is one more thing to rule out when something behaves oddly, and it buys
  // nothing locally.
  if (isLocalhost) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed; the app runs normally without it.', err);
    });
  });
};

// Emergency exit. If a worker ever has to be killed across every installed device, ship a build
// that calls this instead of registerServiceWorker(): it unregisters on next load, and because the
// worker caches nothing, that load is already fresh.
export const unregisterServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
};

// ── Installing to the home screen ────────────────────────────────────────────────────────────────
//
// Captured at module load, not from a component. Chrome fires beforeinstallprompt very early --
// often before React has mounted -- and the event is only offered once. Missing it means the button
// can never appear, so it is caught here and held for whoever asks later.
let deferredPrompt = null;
const installWatchers = new Set();
const notifyWatchers = () => installWatchers.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppresses Chrome's own mini-infobar so the invitation appears where the product decides,
    // rather than in a strip the user has learned to ignore.
    e.preventDefault();
    deferredPrompt = e;
    notifyWatchers();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifyWatchers();
  });
}

export const onInstallStateChange = (fn) => {
  installWatchers.add(fn);
  return () => installWatchers.delete(fn);
};

export const canPromptInstall = () => deferredPrompt !== null;

// Returns 'accepted' | 'dismissed' | 'unavailable'. The event is single-use: once prompt() has been
// called the browser will not hand it back, so it is cleared either way.
export const promptInstall = async () => {
  if (!deferredPrompt) return 'unavailable';
  const event = deferredPrompt;
  deferredPrompt = null;
  notifyWatchers();
  event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
};

// Already running as an installed app -- nothing to invite.
export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches === true
  || window.navigator.standalone === true;

// iOS never fires beforeinstallprompt. Safari has no programmatic install at all, so the only thing
// that works there is telling somebody which two buttons to press -- which is why this is detected
// rather than assumed absent.
export const isIosSafari = () => {
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua)
    // iPadOS reports itself as a Mac; the touch points give it away.
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iOS && webkit;
};

// Which school this window is showing, told to the browser.
//
// Every tenant lives on its own subdomain, so each school is a separate origin and installs as a
// separate app already. What it does NOT get from a static manifest is its own name and badge --
// every school would install as "Digital Administration Portal" with the same purple arch, which is
// most of the point of installing it.
//
// iOS reads these from the live DOM at the moment somebody taps "Add to Home Screen", so setting
// them here is enough there. Chrome reads the manifest, so the manifest link is swapped for a blob
// built from the same values; if a browser refuses the blob, it keeps the static manifest and the
// school simply installs under the platform name. Nothing breaks either way.
export const applySchoolBranding = ({ name, logoUrl, themeColor } = {}) => {
  if (!name) return;

  document.title = name;
  setMeta('apple-mobile-web-app-title', name);
  if (themeColor) setMeta('theme-color', themeColor);
  if (logoUrl) setLink('apple-touch-icon', logoUrl);

  swapManifest({ name, logoUrl, themeColor });
};

const setMeta = (metaName, content) => {
  let el = document.querySelector(`meta[name="${metaName}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', metaName);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

let blobUrl = null;

const swapManifest = ({ name, logoUrl, themeColor }) => {
  const link = document.querySelector('link[rel="manifest"]');
  if (!link) return;

  try {
    // Every URL in here is absolute, and that is not a style preference.
    //
    // A manifest's relative URLs resolve against the manifest's own address -- and a blob: URL
    // cannot be a base: new URL('/', blobUrl) throws outright. A blob manifest written with
    // "start_url": "/" is therefore an INVALID manifest, and the failure is silent and total: no
    // school can install. Absolute URLs remove the question.
    const abs = (path) => new URL(path, window.location.origin).href;

    // A school logo is one image of unknown size, so it is offered in addition to the platform
    // icons rather than instead of them: if it is too small or the wrong shape, the browser still
    // has a 512px icon to fall back on and installability is never at risk.
    const icons = [
      { src: abs('/icons/icon-192.png'), sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: abs('/icons/icon-512.png'), sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: abs('/icons/icon-maskable-512.png'), sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ];
    // The logo is already an absolute Storage download URL, and it is cross-origin, so it is only
    // ever an extra option -- never the only icon.
    if (logoUrl) icons.unshift({ src: logoUrl, sizes: '512x512', type: 'image/png', purpose: 'any' });

    const manifest = {
      id: abs('/'),
      name,
      short_name: name.length > 12 ? `${name.slice(0, 12)}…` : name,
      start_url: abs('/'),
      scope: abs('/'),
      display: 'standalone',
      background_color: '#FAF6EC',
      theme_color: themeColor || '#123A4A',
      icons,
    };

    const next = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }),
    );
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    blobUrl = next;
    link.setAttribute('href', next);
  } catch (err) {
    console.warn('Could not build a per-school manifest; keeping the platform one.', err);
  }
};
